'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { SPARTAN_MEDIA_DEFAULT_IMAGE_DURATION_MS } from '@/app/lcv/components/sections/spartan/shared/hooks/mediaProgress';
interface UseMediaProgressOptions {
  durationMs?: number;
  onProgress: (progress: number) => void;
  onComplete: () => void;
}
export const useMediaProgress = t0 => {
  const $ = _c(20);
  const {
    durationMs: t1,
    onProgress,
    onComplete
  } = t0;
  const durationMs = t1 === undefined ? SPARTAN_MEDIA_DEFAULT_IMAGE_DURATION_MS : t1;
  const timerRef = useRef(null);
  const frameRef = useRef(null);
  const startedAtRef = useRef(null);
  const elapsedRef = useRef(0);
  const completedRef = useRef(false);
  const onProgressRef = useRef(onProgress);
  const onCompleteRef = useRef(onComplete);
  const isRunningRef = useRef(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  let t2;
  let t3;
  if ($[0] !== onComplete || $[1] !== onProgress) {
    t2 = () => {
      onProgressRef.current = onProgress;
      onCompleteRef.current = onComplete;
    };
    t3 = [onComplete, onProgress];
    $[0] = onComplete;
    $[1] = onProgress;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (frameRef.current != null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const cancelScheduledUpdates = t4;
  let t5;
  if ($[5] !== durationMs) {
    t5 = () => {
      if (completedRef.current) {
        return;
      }
      completedRef.current = true;
      startedAtRef.current = null;
      elapsedRef.current = durationMs;
      cancelScheduledUpdates();
      isRunningRef.current = false;
      setIsRunning(false);
      setIsCompleted(true);
      onProgressRef.current(1);
      onCompleteRef.current();
    };
    $[5] = durationMs;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const complete = t5;
  let t6;
  if ($[7] !== complete || $[8] !== durationMs) {
    t6 = () => {
      if (isRunningRef.current) {
        return;
      }
      if (!Number.isFinite(durationMs) || durationMs <= 0) {
        return;
      }
      if (completedRef.current || elapsedRef.current >= durationMs) {
        complete();
        return;
      }
      cancelScheduledUpdates();
      const elapsed = elapsedRef.current;
      const startedAt = performance.now();
      startedAtRef.current = startedAt;
      isRunningRef.current = true;
      setIsRunning(true);
      const tick = now => {
        const progress = Math.min((elapsed + now - startedAt) / durationMs, 1);
        onProgressRef.current(progress);
        if (progress >= 1) {
          frameRef.current = null;
          return;
        }
        frameRef.current = window.requestAnimationFrame(tick);
      };
      frameRef.current = window.requestAnimationFrame(tick);
      timerRef.current = setTimeout(complete, durationMs - elapsed);
    };
    $[7] = complete;
    $[8] = durationMs;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const start = t6;
  let t7;
  if ($[10] !== durationMs) {
    t7 = () => {
      const startedAt_0 = startedAtRef.current;
      if (startedAt_0 != null) {
        elapsedRef.current = Math.min(elapsedRef.current + performance.now() - startedAt_0, durationMs);
        startedAtRef.current = null;
        onProgressRef.current(elapsedRef.current / durationMs);
      }
      cancelScheduledUpdates();
      isRunningRef.current = false;
      setIsRunning(false);
    };
    $[10] = durationMs;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const pause = t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      cancelScheduledUpdates();
      startedAtRef.current = null;
      elapsedRef.current = 0;
      completedRef.current = false;
      isRunningRef.current = false;
      setIsRunning(false);
      setIsCompleted(false);
      onProgressRef.current(0);
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const reset = t8;
  let t10;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => cancelScheduledUpdates;
    t10 = [cancelScheduledUpdates];
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  useEffect(t9, t10);
  let t11;
  if ($[15] !== isCompleted || $[16] !== isRunning || $[17] !== pause || $[18] !== start) {
    t11 = {
      isRunning,
      isCompleted,
      start,
      pause,
      reset
    };
    $[15] = isCompleted;
    $[16] = isRunning;
    $[17] = pause;
    $[18] = start;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
};